import * as dom from '../util/dom';
import * as statsUtil from '../util/stats';
import debugUtil from '../util/debug';

const Debug = debugUtil('akamaiStats');

const NAMESPACE = 'akamaiStats.';
const instances = new Map();

function loadLibs() {
    window.AKAMAI_MEDIA_ANALYTICS_CONFIG_FILE_PATH = 'https://ma291-r.analytics.edgekey.net/config/beacon-8803.xml?enableGenericAPI=1';

    return dom.getScript('https://79423.analytics.edgekey.net/js/csma.js');
}

class AkaHTML5AnalyticsGetters {
    constructor(api) {
        this.api = api;
    }

    // Below is used by statistics lib

    // noinspection JSUnusedGlobalSymbols
    streamHeadPosition() {
        return this.api.getCurrentTime();
    }

    // noinspection JSUnusedGlobalSymbols
    streamLength() {
        return this.api.getDuration();
    }

    // noinspection JSUnusedGlobalSymbols
    streamURL() {
        return this.api.getSrc();
    }
}

class AkamaiStats {
    constructor(api) {
        this.api = api;
        this.akamai = null;
        this.currentSrc = null;
        this.enabled = false;
        this.eventQueue = [];

        this.sessionInitialized = false;
        this.wasPlayed = false;
        this.isBuffering = false;

        this.api.filters.push({
            load: (videoId, autoplay, options) => {
                if (options.chromecast) {
                    this.disable();
                } else {
                    this.enable();
                }

                this.wasPlayed = false;
            }
        });

        loadLibs();
    }

    enable() {
        if (this.enabled) {
            return;
        }

        this.enabled = true;

        loadLibs().then(() => {
            if (!this.enabled) {
                return;
            }

            this.akamai = new window.AkaHTML5MediaAnalytics(new AkaHTML5AnalyticsGetters(this.api));
            this.akamai.setData('viewerId', 'unknown viewer');

            this.loaded = true;

            // TODO: Only do below for SVT CDN
            this.akamai.disableServerIpLookUp();
            this._drainEventQueue();
        });

        this.api.on(NAMESPACE + 'playing', this.onPlaying.bind(this));
        this.api.on(NAMESPACE + 'play', this.onPlay.bind(this));
        this.api.on(NAMESPACE + 'pause', this.onPause.bind(this));
        this.api.on(NAMESPACE + 'bufferEmpty', this.onBufferEmpty.bind(this));
        this.api.on(NAMESPACE + 'bitrateswitch', this.onBitrateSwitch.bind(this));
        this.api.on(NAMESPACE + 'ended', this.onEnded.bind(this));
        this.api.on(NAMESPACE + 'error', this.onError.bind(this));
        this.api.on(NAMESPACE + 'metadata', this.onMetadata.bind(this));
    }

    onPlaying() {
        if (!this.loaded || !this.sessionInitialized) {
            this.eventQueue.push({ func: 'onPlaying' });

            return;
        }

        if (!this.wasPlayed) {
            this.akamai.handlePlaying();
            this.wasPlayed = true;
        }

        if (this.isBuffering) {
            this.akamai.handleBufferEnd();
            this.isBuffering = false;
        }
    }

    onPause() {
        if (!this.loaded) {
            this.eventQueue.push({ func: 'onPause' });

            return;
        }

        this.akamai.handlePause();
    }

    onPlay() {
        if (!this.loaded) {
            this.eventQueue.push({ func: 'onPlay' });

            return;
        }

        if (this.wasPlayed) {
            this.akamai.handleResume();
        } else if (this.sessionInitialized) {
            this.akamai.handleStreamSwitch();
            this.currentSrc = this.api.getSrc();
        } else {
            this.akamai.handleSessionInit();
            this.sessionInitialized = true;
            Debug.log('session init');
            this._drainEventQueue();
        }
    }

    onBufferEmpty() {
        if (!this.loaded) {
            this.eventQueue.push({ func: 'onBufferEmpty' });

            return;
        }

        if (!this.isBuffering && this.api.video().seeking === false) {
            this.akamai.handleBufferStart();
            this.isBuffering = true;
        }
    }

    onBitrateSwitch(e) {
        if (!this.loaded || !this.sessionInitialized) {
            this.eventQueue.push({ func: 'onBitrateSwitch', args: [e] });

            return;
        }

        this.akamai.handleBitRateSwitch(e.detail.bitrate);
    }

    onEnded() {
        if (!this.loaded) {
            this.eventQueue.push({ func: 'onEnded' });

            return;
        }

        this.akamai.handlePlayEnd('Play.End.Detected');
    }

    onError(err) {
        if (!this.loaded) {
            this.eventQueue.push({ func: 'onError', args: [err] });

            return;
        }

        if (err && err.detail.isExpectedError === true) {
            return;
        }

        this.akamai.handleError((err && err.detail.name) ? err.detail.name : 'Unknown error');
    }

    onMetadata(e) {
        if (!this.loaded) {
            this.eventQueue.push({ func: 'onMetadata', args: [e] });

            return;
        }

        const metadata = e.detail;

        const client = statsUtil.getClient();
        this.akamai.setData('device', navigator.platform);
        this.akamai.setData('playerId', 'svtvideoplayer-html5-' + client);

        if (metadata.mmsStatistics) {
            const show = metadata.mmsStatistics.ns_st_pr;
            const title = show + ' - ' + metadata.mmsStatistics.ns_st_ep + ' (' + metadata.programVersionId + ')';

            this.akamai.setData('title', title);
            this.akamai.setData('show', show);
            this.akamai.setData('eventName', title);
        }
    }

    _drainEventQueue() {
        for (let i = 0; i < this.eventQueue.length; i++) {
            const queueItem = this.eventQueue[i];
            if (this.sessionInitialized || queueItem.func === 'onPlay') {
                Debug.log('event ' + queueItem.func);
                this[queueItem.func].apply(this, queueItem.args);
                this.eventQueue.splice(i, 1);
                i--;
            }
        }
    }

    disable() {
        if (!this.enabled) {
            return;
        }

        this.enabled = false;
        this.loaded = false;

        this.api.off(NAMESPACE + 'playing');
        this.api.off(NAMESPACE + 'pause');
        this.api.off(NAMESPACE + 'play');
        this.api.off(NAMESPACE + 'waiting');
        this.api.off(NAMESPACE + 'bitrateswitch');
        this.api.off(NAMESPACE + 'ended');
        this.api.off(NAMESPACE + 'error');
        this.api.off(NAMESPACE + 'metadata');

        if (this.akamai) {
            this.akamai.handleApplicationExit();
            this.akamai = null;
        }
    }

    destroy() {
        this.disable();
        this.initialized = false;
        this.currentSrc = null;
    }
}

export { NAMESPACE as namespace };

export function create(api) {
    if (instances.has(api)) {
        Debug.warn(
            `Akamai statistics plugin was already created for this api instance and has
            not been destroyed. The previous instance may leak memory and listeners.`
        );
    }

    const instance = new AkamaiStats(api);
    instances.set(api, instance);

    return instance;
}

export function destroy(api) {
    if (instances.has(api)) {
        instances.get(api).destroy();
        instances.delete(api);
    }
}



// WEBPACK FOOTER //
// ./src/js/plugins/akamai-stats.js